import type { TeamGame } from "../../../shared/news";
import { LEAGUES } from "../../../shared/news";
import { useHousehold } from "../../app/useHousehold";
import { useTeam } from "../../data/newsQueries";
import { dataStateOf } from "../../data/queries";
import { EmptyState } from "../../ui/StatusBadge";
import { formatShortTime, useNow } from "../../ui/time";
import { remPx, SkyFoot } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { ArticleLink } from "./ArticleSheet";
import { WidgetHeader } from "../WidgetHeader";
import { untilText } from "./F1Widget";
import { NewsImage, storyAge } from "./newsUi";

interface TeamConfig {
  /** Which of the household's teams (Admin → News & sports), 1-based. */
  slot: number;
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

/** One of the family's teams: record, the next game and the last result, the standings and team news. */
function Team({ config, size }: WidgetProps<TeamConfig>) {
  const { settings, timeZone, isAdmin } = useHousehold();
  const now = useNow("minute");
  const ref = settings.news.teams[config.slot - 1] ?? null;
  const q = useTeam(ref);
  const state = dataStateOf(q, 6 * 60 * 60_000);
  if (!ref)
    return (
      <EmptyState
        icon="trophy"
        title="Pick a team"
        detail={`Add your teams in Admin → News & sports; this card shows team ${config.slot}.`}
        action={
          isAdmin ? (
            <a className="btn btn-sm btn-primary" href="/admin#news">
              Pick teams
            </a>
          ) : undefined
        }
      />
    );
  const d = q.data;
  if (!d) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="trophy" title="Scores unavailable" detail={q.error?.message} />;
  const rem = remPx();
  const place = d.standings ? d.standings.rows.findIndex((r) => r.isTeam) + 1 : 0;
  const standing = d.team.standing ?? (place > 0 ? `${ordinal(place)} in ${d.standings!.group}` : null);
  const when = (g: TeamGame) => {
    const date = new Date(g.date);
    const day = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", month: "short", day: "numeric" }).format(date);
    return `${day} · ${formatShortTime(date, timeZone, settings.clock24h)}`;
  };
  const showTable = d.standings && size.height > rem * 20;
  const newsRows = Math.max(0, Math.floor((size.height - rem * (showTable ? 26 : 16)) / (rem * 3.6)));

  return (
    <div className="news-sport team" style={{ ["--team" as string]: d.team.color ?? "var(--accent)" }}>
      <WidgetHeader
        title={
          <span className="team-title">
            <NewsImage src={d.team.logo} className="team-logo" alt="" />
            {d.team.name}
          </span>
        }
        meta={[d.team.record, standing].filter(Boolean).join(" · ") || LEAGUES[d.league].name}
      />
      {d.next && (
        <div className="team-game">
          <span className="news-eyebrow">{d.next.state === "in" ? "Live now" : "Next game"}</span>
          <span className="team-vs">
            <span className="muted">{d.next.home ? "vs" : "at"}</span>
            <NewsImage src={d.next.opponent.logo} className="team-logo-lg" alt="" />
            <strong>{d.next.opponent.name}</strong>
            {d.next.result && <b className="team-score">{d.next.result}</b>}
          </span>
          <span>
            {when(d.next)} {d.next.state === "pre" && <span className="f1-until">{untilText(d.next.date, now)}</span>}
          </span>
          <span className="muted small">{[d.next.broadcast && `📺 ${d.next.broadcast}`, d.next.venue].filter(Boolean).join(" · ")}</span>
        </div>
      )}
      {d.last && (
        <p className="team-last">
          <span className="news-eyebrow">Last</span>{" "}
          <b className={d.last.result?.startsWith("W") ? "win" : d.last.result?.startsWith("L") ? "loss" : ""}>{d.last.result}</b> {d.last.home ? "vs" : "at"} {d.last.opponent.name}
        </p>
      )}
      {showTable && (
        <table className="team-table">
          <caption>{d.standings!.group}</caption>
          <tbody>
            {d.standings!.rows.slice(0, 8).map((r) => (
              <tr key={r.abbr} className={r.isTeam ? "is-team" : ""}>
                <td>
                  <NewsImage src={r.logo} className="team-logo-sm" alt="" />
                  {r.name}
                </td>
                <td>{r.record}</td>
                <td className="muted">{r.gb && r.gb !== "-" && r.gb !== "0" ? r.gb : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {newsRows > 0 && d.news.length > 0 && (
        <ul className="team-news">
          {d.news.slice(0, newsRows).map((s) => (
            <li key={s.id}>
              {s.url ? (
                <ArticleLink story={s} className="news-row">
                  <NewsImage src={s.image} className="news-thumb" />
                  <span className="news-row-text">
                    <span className="news-row-title">{s.title}</span>
                    <span className="news-row-meta">{storyAge(s)}</span>
                  </span>
                </ArticleLink>
              ) : (
                <span className="news-row-title">{s.title}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <SkyFoot source="ESPN" state={state} updatedAt={q.dataUpdatedAt} />
    </div>
  );
}

export const teamWidget: WidgetDefinition<TeamConfig> = {
  type: "team",
  version: 1,
  name: "Team",
  description: "One of your teams: record, next game and TV channel, last result, standings and team news.",
  icon: "trophy",
  category: "household",
  defaultSize: { w: 4, h: 6 },
  minSize: { w: 3, h: 3 },
  stackHeight: 520,
  defaults: { slot: 1 },
  settings: [{ key: "slot", label: "Team", type: "select", options: [1, 2, 3, 4].map((n) => ({ value: n, label: `Team ${n} (Admin → News & sports)` })) }],
  provider: "espn",
  Component: Team,
};
